import { map } from 'lodash'
import React from 'react'
import styled, { css } from 'styled-components'
import dayjs from 'dayjs'
import { KeyTextField, LinkField } from '@prismicio/client'

import { AutoSpacer } from '../../../slices/Spacer'
import { BackgroundColorKey, backgroundColors } from '../../styles/backgroundColors'
import { media } from '../../utils/style-utils'
import BreadCrumbs, { ICrumb } from '../BreadCrumbs'
import CardContainer from '../CardContainer'
import QuickLink, { IQuickLinkColors } from '../QuickLink'
import { getLinkUrl } from '../../utils/ts-helpers'

interface IPageTitle {
	heading: string
	subtitle: string
	isSubtitleDate?: boolean
	show_breadcrumbs: boolean
	background_color?: BackgroundColorKey
	crumbs?: ICrumb[]
	center_alignment?: boolean
	repeatableItems?: {
		link_to_page: LinkField
		link_text: KeyTextField
	}[]
}

const PageTitle: React.FC<IPageTitle> = ({
	heading,
	subtitle,
	isSubtitleDate = false,
	show_breadcrumbs,
	background_color,
	crumbs,
	center_alignment = false,
	repeatableItems,
}) => {
	const backgroundColor = backgroundColors(background_color ?? 'White')
	const showBreadcrumbs = show_breadcrumbs && Array.isArray(crumbs)
	const quickLinkColors = getQuickLinkColors(backgroundColor)
	return (
		<>
			<StyledCardContainer
				options={{ backgroundColor }}
				animation={false}
				isCentered={center_alignment}
			>
				<Container>
					<TextWrapper backgroundColor={backgroundColor}>
						{showBreadcrumbs && <BreadCrumbs crumbs={crumbs} />}
						<Heading>{heading}</Heading>
						<Subtitle>
							{isSubtitleDate && dayjs(subtitle).isValid() ? dayjs(subtitle).format('DD.MM.YYYY') : subtitle}
						</Subtitle>
					</TextWrapper>
					{repeatableItems && (
						<QuickLinks>
							{map(repeatableItems, ({ link_to_page, link_text }) => {
								if (!(!link_to_page || getLinkUrl(link_to_page) === '#' || !link_text))
									return (
										<QuickLink
											key={link_text}
											url={getLinkUrl(link_to_page)}
											colors={quickLinkColors}
										>
											{link_text}
										</QuickLink>
									)
							})}
						</QuickLinks>
					)}
				</Container>
			</StyledCardContainer>
			<AutoSpacer negative={false} />
		</>
	)
}

const getQuickLinkColors = (backgroundColor: string) => {
	let quickLinkColors: IQuickLinkColors = {
		text: undefined,
		background: undefined,
	}

	if (backgroundColor === backgroundColors('Orange')) {
		quickLinkColors = {
			background: 'var(--deep-blue)',
			text: backgroundColor['Neon'],
		}
	} else if (backgroundColor === backgroundColors('Blue (100)')) {
		quickLinkColors = {
			background: 'var(--deep-blue)',
			text: 'var(--neon)',
		}
	} else if (backgroundColor === backgroundColors('Neon')) {
		quickLinkColors = {
			background: 'var(--deep-blue)',
			text: 'var(--neon)',
		}
	}
	return quickLinkColors
}

const TextWrapper = styled.div<{ backgroundColor: string }>`
	/* margin-bottom: 112px; */
	display: flex;
	flex-direction: column;
	gap: 24px;

	${(p) =>
		p.backgroundColor === 'var(--deep-blue)' &&
		css`
			color: var(--neon);
		`};

	${media.phone`
		gap: 16px;
	`};
`

const StyledCardContainer = styled(CardContainer)<{ isCentered: boolean }>`
	padding-bottom: 80px;
	${(props) =>
		props.isCentered &&
		css`
			@media (min-width: 870px) {
				grid-column: 2 / -2;
				display: grid;
				grid-template-columns: 1fr 720px 1.6fr;

				${TextWrapper} {
					display: grid;
					grid-column-start: 2;
					max-width: 65ch;
				}
			}
		`};

	${media.phone`
		padding-bottom: 32px;
	`}
`

const Container = styled.div`
	grid-column: 2 / -2;
	margin-top: 216px;
	display: flex;
	justify-content: 'flex-end';

	flex-direction: column;
	gap: 112px;

	${media.tablet`

		margin-top: var(--spacer-xl);
	`}
	${media.phone`
		margin-top: 72px;
		gap: 72px;
		padding-bottom: 0;
	`}
`

const Heading = styled.h1`
	margin: 0;

	${media.desktopLg`
			font-size: 75px;
	`}
	${media.phone`
		font-size: 56px;
	`}
`

const Subtitle = styled.h3`
	margin: 0;
	font-size: 2rem;
	font-weight: 200;
	white-space: break-spaces;

	${media.phone`
		font-size: 1.5rem;
		white-space: normal;
	`}
`

const QuickLinks = styled.div`
	display: flex;
	gap: var(--spacer-sm);

	${media.phone`
		flex-direction: column;
		align-items: flex-start;
	`}
`

export default PageTitle
